import { ArrowRight, Check } from "lucide-react";
import { useState } from "react";
import { Reveal } from "@/components/reveal";
import {
	formatAccountLimit,
	formatLimit,
	PLAN_CAPABILITIES,
	PUBLIC_PRICING_TIERS,
	SUBSCRIPTION_DEFINITIONS,
	type SubscriptionTier,
} from "@/lib/plans";
import { cn } from "@/lib/utils";

type BillingPeriod = "monthly" | "yearly";

const tiers: SubscriptionTier[] = [...PUBLIC_PRICING_TIERS];

function yearlySavingsEUR(tier: SubscriptionTier): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	return Math.max(sub.priceEUR * 12 - sub.yearlyPriceEUR, 0);
}

function buildFeatures(tier: SubscriptionTier): string[] {
	const cap = PLAN_CAPABILITIES[tier];
	const base = [
		`${formatAccountLimit(cap.maxConnectedSocialAccounts)} connected accounts`,
		`${formatLimit(cap.maxScheduledPosts)} scheduled posts / mo`,
		`${cap.maxWorkspaces} workspace${cap.maxWorkspaces > 1 ? "s" : ""}`,
		`${cap.maxTeamMembers} team member${cap.maxTeamMembers > 1 ? "s" : ""}`,
	];
	if (cap.listeningEnabled) {
		base.push(
			`Social listening: ${cap.maxListeningKeywords} keywords (Reddit, Bluesky, YouTube)`,
		);
	}
	if (tier === "pro") {
		return [
			...base,
			"Plan and publish from chat",
			"Calendar scheduling and queue",
			"Per-platform caption overrides",
			"API & MCP access",
			"Email support",
		];
	}
	return [
		...base,
		"Plan and publish from chat",
		"Calendar scheduling and queue",
		"Per-platform caption overrides",
		"API & MCP access",
		"Team approvals",
		"Priority support",
	];
}

function planCta(tier: SubscriptionTier): { label: string; href: string } {
	if (tier === "pro" || tier === "max") {
		return {
			label: `Subscribe to ${tier === "pro" ? "Pro" : "Max"}`,
			href: "/choose-plan",
		};
	}
	return { label: "Start now", href: "/choose-plan" };
}

export function PricingTable({ className }: { className?: string }) {
	const [period, setPeriod] = useState<BillingPeriod>("monthly");

	return (
		<div className={cn(className)}>
			<h2 className="sr-only">Plans</h2>
			<div className="grid grid-cols-1 gap-px bg-border sm:grid-cols-2">
				{tiers.map((tier) => {
					const sub = SUBSCRIPTION_DEFINITIONS[tier];
					const monthlyPrice =
						period === "yearly"
							? Math.round(sub.yearlyPriceEUR / 12)
							: sub.priceEUR;
					const displayPrice =
						period === "yearly" ? sub.yearlyPriceEUR : monthlyPrice;
					const features = buildFeatures(tier);
					const cta = planCta(tier);
					const isHighlighted = !!sub.highlighted;
					const savings = yearlySavingsEUR(tier);
					const showPeriodToggle = tier === "pro";

					return (
						<div
							key={tier}
							className={cn(
								"homepage-hover-cell relative flex flex-col rounded-md bg-white dark:bg-black p-6 sm:p-8",
								isHighlighted && "ring-1 ring-blue-600/20",
							)}
						>
							<Reveal
								className="flex h-full flex-col"
								delay={tier === "max" ? 80 : 0}
							>
								{showPeriodToggle ? (
									<div className="mb-6 inline-flex self-start rounded-full border border-border bg-muted/40 p-1 text-sm">
										<button
											type="button"
											onClick={() => setPeriod("monthly")}
											className={cn(
												"rounded-full px-4 py-1.5 font-medium transition-colors",
												period === "monthly"
													? "bg-background text-foreground shadow-sm"
													: "text-muted-foreground hover:text-foreground",
											)}
										>
											Monthly
										</button>
										<button
											type="button"
											onClick={() => setPeriod("yearly")}
											className={cn(
												"inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-medium transition-colors",
												period === "yearly"
													? "bg-background text-foreground shadow-sm"
													: "text-muted-foreground hover:text-foreground",
											)}
										>
											Annually
											<span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold leading-none text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
												2 months free
											</span>
										</button>
									</div>
								) : (
									<div aria-hidden className="mb-6 hidden h-[34px] sm:block" />
								)}
								<div className="flex items-center gap-2">
									<h3 className="text-lg font-semibold tracking-tight text-foreground">
										{sub.name}
									</h3>
									{isHighlighted && (
										<span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
											Most Popular
										</span>
									)}
								</div>

								<p className="mt-3 min-h-[2.5rem] max-w-[30ch] text-sm leading-relaxed text-muted-foreground">
									{sub.description}
								</p>

								<div className="mt-6 flex items-baseline gap-1">
									<span className="text-lg font-medium text-foreground">€</span>
									<span className="text-4xl font-semibold tracking-[-0.04em] text-foreground">
										{displayPrice}
									</span>
									{period === "yearly" && (
										<span className="text-sm font-medium text-muted-foreground">
											/yr
										</span>
									)}
								</div>
								<p className="mt-1 text-xs text-muted-foreground">
									{period === "yearly"
										? `€${monthlyPrice}/mo equivalent. Pay for 10 months, get 2 months free and save €${savings}/yr.`
										: "Per month"}
								</p>

								<ul className="mt-6 space-y-2.5">
									{features.map((feature) => (
										<li
											key={feature}
											className="flex items-start gap-3 text-sm text-foreground"
										>
											<Check
												className={cn(
													"mt-0.5 size-4 shrink-0",
													isHighlighted
														? "text-blue-600 dark:text-blue-400"
														: "text-muted-foreground",
												)}
											/>
											<span>{feature}</span>
										</li>
									))}
								</ul>

								<div className="mt-auto pt-8">
									<a
										href={cta.href}
										className={cn(
											"inline-flex w-full items-center justify-center gap-2 rounded-md border px-4 py-2.5 text-sm font-medium transition-colors",
											isHighlighted
												? "border-transparent bg-blue-600 text-white hover:bg-blue-700"
												: "border-border bg-background text-foreground hover:bg-muted/60",
										)}
									>
										{cta.label}
										{isHighlighted && <ArrowRight className="size-4" />}
									</a>
								</div>
							</Reveal>
						</div>
					);
				})}
			</div>
		</div>
	);
}
